/**
 * Dairy & pathway registry — the supply side of the allocation.
 *
 * The mirror of the FSE registry. The allocation engine merges two ordered
 * rosters: dairy-vintage lots against station capacities. `/lcfs/fse` is the
 * roster of the demand side; this is the other half, and the ledger is the two
 * of them merged.
 *
 * Rows appear in **allocation draw order**, not alphabetically, because that
 * order is the screen's point. It is the sequence the engine consumes dairies
 * in, and the dairy at the bottom is the one starved when a month's claimed
 * capacity falls short of production — in August 2025 the shortfall was
 * 1,654.50 MMBtu and Martin Bro's, last in the roster, produced 1,654.54.
 *
 * Backend: frontend-first. `GET /lcfs/dairies` is served by MSW.
 */

import {
  Alert,
  Badge,
  Group,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { Link, useSearchParams } from "react-router-dom";
import { useDairyRegistry } from "../api/hooks";
import { DairyHistoryDrawer } from "../components/lcfs/DairyHistoryDrawer";
import { StationHistoryDrawer } from "../components/lcfs/StationHistoryDrawer";
import { useLcfsDrilldown } from "../components/lcfs/useLcfsDrilldown";

import type { DairyRecord, PathwayStatus } from "../api/types";
import { formatApiError } from "../lib/errors";

const STATUS_COLOR: Record<PathwayStatus, string> = {
  certified: "teal",
  temporary: "orange",
  pending: "red",
};

const STATUS_LABEL: Record<PathwayStatus, string> = {
  certified: "Certified",
  temporary: "Temporary",
  pending: "No code",
};

const STATUS_EXPLANATION: Record<PathwayStatus, string> = {
  certified:
    "A certified CARB pathway with its own carbon intensity. Credits claimed against it are settled.",
  temporary:
    "A real CARB code carrying a conservative interim CI while certification is pending. Volume reported under it may need restating once the pathway certifies at its final CI.",
  pending:
    "A placeholder, not a CARB identifier. Volume cannot be filed under it at all — this dairy has no reportable pathway yet.",
};

export function PathwayRegistryPage() {
  // Filter is state-of-record in the URL (locked convention).
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";

  const setParam = (key: string, value: string) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  const dairiesQ = useDairyRegistry();
  const records = dairiesQ.data?.items ?? [];
  const drill = useLcfsDrilldown();
  const filtered = status ? records.filter((d) => d.pathway_status === status) : records;

  const counts = (s: PathwayStatus) => records.filter((d) => d.pathway_status === s).length;
  const notProducing = records.filter((d) => d.first_production_month === null).length;

  return (
    <Stack gap="lg">
      {/* No section heading: the tab label is it — see `routes/Lcfs.tsx`. */}
      <Stack gap={4}>
        <Text c="dimmed" size="sm" maw={840}>
          One record per dairy, in the order the allocation draws them. This is the supply side of
          the ledger — the <Link to="/lcfs?tab=fse">FSE registry</Link> is the demand side, and the
          allocation is the two rosters merged. A dairy's fuel pathway code is its regulatory
          identity, the way an FSE ID is a dispenser's.
        </Text>
      </Stack>

      <Paper p="md" radius="md" withBorder>
        <Group gap="md" wrap="wrap" align="flex-end">
          <Select
            label="Pathway status"
            data={[
              { value: "", label: "All pathways" },
              { value: "certified", label: `Certified (${counts("certified")})` },
              { value: "temporary", label: `Temporary (${counts("temporary")})` },
              { value: "pending", label: `No code (${counts("pending")})` },
            ]}
            value={status}
            onChange={(v) => setParam("status", v ?? "")}
            w={220}
            allowDeselect={false}
          />
          <Text size="sm" c="dimmed">
            {filtered.length} of {records.length} dairies
            {notProducing > 0 ? ` · ${notProducing} not yet producing` : ""}
          </Text>
        </Group>
      </Paper>

      {dairiesQ.isPending ? (
        <Skeleton height={420} radius="md" />
      ) : dairiesQ.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">{formatApiError(dairiesQ.error)}</Text>
        </Alert>
      ) : (
        <DairyTable records={filtered} onOpen={drill.openDairy} />
      )}

      <StationHistoryDrawer
        fseId={drill.stationId}
        onClose={drill.closeStation}
        onOpenDairy={drill.openDairy}
      />
      <DairyHistoryDrawer
        dairyId={drill.dairyId}
        onClose={drill.closeDairy}
        onOpenStation={drill.openStation}
      />
    </Stack>
  );
}

function DairyTable({
  records,
  onOpen,
}: {
  records: DairyRecord[];
  onOpen: (dairyId: string) => void;
}) {
  if (records.length === 0) {
    return (
      <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">No dairies match this filter.</Text>
      </Alert>
    );
  }

  return (
    <Paper radius="md" withBorder>
      <ScrollArea>
        <Table striped highlightOnHover verticalSpacing="sm" miw={940}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={60}>Draw</Table.Th>
              <Table.Th>Dairy</Table.Th>
              <Table.Th>Fuel pathway code</Table.Th>
              <Table.Th>Pathway status</Table.Th>
              <Table.Th ta="right">Certified CI</Table.Th>
              <Table.Th>RIN eligible</Table.Th>
              <Table.Th>Producing since</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {records.map((d) => (
              <Table.Tr
                key={d.dairy_id}
                onClick={() => onOpen(d.dairy_id)}
                style={{ cursor: "pointer" }}
              >
                <Table.Td>
                  <Tooltip
                    label="Position in the allocation draw order. The dairy sorting last is the one starved when a month's claimed capacity falls short of production."
                    multiline
                    w={300}
                    withArrow
                  >
                    <Text
                      size="sm"
                      ff="monospace"
                      c="dimmed"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {d.roster_position + 1}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm">{d.name}</Text>
                    {d.pathway_note ? (
                      <Text size="xs" c="dimmed">
                        {d.pathway_note}
                      </Text>
                    ) : null}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {d.fpc_code ? (
                    <Text
                      size="sm"
                      ff="monospace"
                      c={d.pathway_status === "temporary" ? "orange" : undefined}
                    >
                      {d.fpc_code}
                    </Text>
                  ) : (
                    <Tooltip
                      label="The source sheet carries 'Test Code CNGPENDING' here. A test code is not a CARB identifier and nothing can be filed under it."
                      multiline
                      w={300}
                      withArrow
                    >
                      <Text size="xs" c="red">
                        none assigned
                      </Text>
                    </Tooltip>
                  )}
                </Table.Td>
                <Table.Td>
                  <Tooltip label={STATUS_EXPLANATION[d.pathway_status]} multiline w={320} withArrow>
                    <Badge
                      color={STATUS_COLOR[d.pathway_status]}
                      variant={d.pathway_status === "certified" ? "light" : "filled"}
                      size="sm"
                    >
                      {STATUS_LABEL[d.pathway_status]}
                    </Badge>
                  </Tooltip>
                </Table.Td>
                <Table.Td ta="right">
                  {d.certified_ci_gco2e_per_mj ? (
                    <Tooltip
                      label={
                        d.pathway_status === "temporary"
                          ? "A conservative interim value CARB applies while certification is pending, not this dairy's real carbon intensity."
                          : "Certified value from the CARB pathway. The workbook's own lookup column carries -150 for every dairy, including this one — that figure is the interim placeholder and is wrong here."
                      }
                      multiline
                      w={320}
                      withArrow
                    >
                      <Text
                        size="sm"
                        ff="monospace"
                        style={{ fontVariantNumeric: "tabular-nums" }}
                        c={d.pathway_status === "temporary" ? "orange" : undefined}
                      >
                        {d.certified_ci_gco2e_per_mj}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text size="xs" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {d.rin_eligibility_date ? (
                    <Text size="xs" ff="monospace">
                      {d.rin_eligibility_date}
                    </Text>
                  ) : (
                    <Tooltip
                      label="No RFS eligibility date on file — onboarding is incomplete."
                      withArrow
                    >
                      <Badge color="gray" variant="outline" size="sm">
                        pending
                      </Badge>
                    </Tooltip>
                  )}
                </Table.Td>
                <Table.Td>
                  {d.first_production_month ? (
                    <Text size="xs" ff="monospace" c="dimmed">
                      {d.first_production_month}
                    </Text>
                  ) : (
                    <Tooltip
                      label="This dairy has never booked production in any period the ledger covers."
                      withArrow
                    >
                      <Text size="xs" c="dimmed">
                        not yet
                      </Text>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}
